import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { usesOf } from "@/pages/LibraryPage";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const EMPTY = { manufacturer: "", brand: "", line: "", shade: "", aliases: "", region: "", batch: "", revision_date: "", inci: "", ci_numbers: "", source_url: "", sds_url: "", source_note: "", verification_status: "unverified", technique_notes: "", area: "brows", uses: [], description: "", sku: "", formula_version: "", status: "active", last_reviewed: "" };
const TEXT_FIELDS = [
  ["brand", "Brand *"], ["manufacturer", "Manufacturer"], ["line", "Line"], ["shade", "Shade"], ["aliases", "Aliases (; separated)"],
  ["region", "Region"], ["batch", "Batch / lot"], ["revision_date", "Revision date"], ["ci_numbers", "CI numbers (; separated)"],
  ["source_url", "Manufacturer source URL"], ["sds_url", "SDS URL"], ["last_reviewed", "Last reviewed (YYYY-MM-DD)"], ["sku", "SKU"],
];

const USE_OPTS = [["brows", "Brows"], ["lips", "Lips"], ["eyeliner", "Eyeliner"], ["correction", "Toning / correction"], ["areola", "Areola"], ["scar", "Scar camouflage"]];

function Sel({ value, onChange, opts, testId }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testId} className="h-10 rounded-xl border-[#E6DBCF]"><SelectValue /></SelectTrigger>
      <SelectContent>{opts.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
    </Select>
  );
}

export function PigmentDialog({ open, onOpenChange, pigment, onSaved }) {
  const [f, setF] = useState(EMPTY);
  useEffect(() => {
    setF(pigment ? { ...EMPTY, ...pigment, uses: usesOf(pigment), aliases: (pigment.aliases || []).join("; "), ci_numbers: (pigment.ci_numbers || []).join("; ") } : EMPTY);
  }, [pigment, open]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target ? e.target.value : e });

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...f, area: f.uses[0] || "unspecified", aliases: f.aliases.split(";").map((s) => s.trim()).filter(Boolean), ci_numbers: f.ci_numbers.split(/[;,]/).map((s) => s.trim()).filter(Boolean) };
    ["id", "image_file_id", "formulation_tags", "formulation_category", "created_at", "updated_at", "data_source"].forEach((k) => delete payload[k]);
    try {
      if (pigment) await api.put(`/admin/pigments/${pigment.id}`, payload);
      else await api.post("/admin/pigments", payload);
      toast.success("Pigment record saved");
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto bg-[#FDFBF7]" data-testid="pigment-dialog">
        <DialogHeader><DialogTitle className="font-serif text-3xl">{pigment ? "Edit pigment record" : "New pigment record"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="grid gap-3 sm:grid-cols-2">
          {TEXT_FIELDS.map(([k, l]) => (
            <label key={k} className="block">
              <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#A88B7D]">{l}</span>
              <Input data-testid={`pigment-field-${k}`} required={k === "brand"} value={f[k]} onChange={set(k)} className="mt-1 h-10 rounded-xl border-[#E6DBCF]" />
            </label>
          ))}
          <div className="sm:col-span-2">
            <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#A88B7D]">Intended use (select all that apply; none = not verified by source)</span>
            <div className="mt-1.5 flex flex-wrap gap-2" data-testid="pigment-field-uses">
              {USE_OPTS.map(([v, l]) => {
                const on = f.uses.includes(v);
                return (
                  <button type="button" key={v} data-testid={`pigment-use-toggle-${v}`} aria-pressed={on}
                    onClick={() => setF({ ...f, uses: on ? f.uses.filter((u) => u !== v) : [...f.uses, v] })}
                    className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${on ? "border-[#2C1810] bg-[#2C1810] text-[#FDFBF7]" : "border-[#E6DBCF] text-[#6E5549] hover:border-[#C5A059]"}`}>{l}</button>
                );
              })}
            </div>
          </div>
          <label className="block">
            <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#A88B7D]">Verification status</span>
            <Sel testId="pigment-field-verification_status" value={f.verification_status} onChange={set("verification_status")} opts={[["verified_shade", "Verified shade-level"], ["partial", "Partial"], ["unverified", "Unverified (CI)"], ["name_only", "Name only"]]} />
          </label>
          <label className="block">
            <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#A88B7D]">Record status</span>
            <Sel testId="pigment-field-status" value={f.status} onChange={set("status")} opts={[["active", "Active"], ["reformulated", "Reformulated"], ["retired", "Retired"]]} />
          </label>
          {[["description", "Manufacturer description"], ["formula_version", "Formula / version"], ["inci", "Full INCI ingredients"], ["source_note", "Source / evidence note"], ["technique_notes", "Technique notes (evidence-based cautions)"]].map(([k, l]) => (
            <label key={k} className="block sm:col-span-2">
              <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#A88B7D]">{l}</span>
              <Textarea data-testid={`pigment-field-${k}`} value={f[k]} onChange={set(k)} rows={2} className="mt-1 rounded-xl border-[#E6DBCF]" />
            </label>
          ))}
          <p className="text-[11px] text-[#6E5549] sm:col-span-2">Formulation tags and category are derived automatically from the documented CI numbers.</p>
          <button type="submit" data-testid="pigment-save-btn" className="h-11 rounded-full bg-[#2C1810] text-sm font-semibold text-[#FDFBF7] hover:bg-[#3D2317] sm:col-span-2">Save record</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
